import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Spinner, Modal } from 'react-bootstrap';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTrash, faPen, faUser, faTimes, 
  faRotateLeft, faExpand, faArrowsAlt, faInfo,
  faLock
} from '@fortawesome/free-solid-svg-icons';
import { PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { useMutation } from '@tanstack/react-query';
import { usePermissionModal } from 'contexts/PermissionModalContext';

interface ImageUploaderProps {
  endpoint: string;
  imageUrl: string | null;
  onImageUploaded: (imageUrl: string | null) => void;
  size?: 'sm' | 'md' | 'lg';
  shape?: 'circle' | 'square';
  className?: string;
  nameInitial?: string;
  aspectRatio?: number; // Optional aspect ratio for the crop
  isNewUser?: boolean; // Added prop to indicate if this is for a new user
}

const ImageUploader: React.FC<ImageUploaderProps> = ({
  endpoint,
  imageUrl,
  onImageUploaded,
  size = 'md',
  shape = 'circle',
  className = '',
  nameInitial = '',
  aspectRatio,
  isNewUser = false, // Default to false
}) => {
  // State for component
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [displayUrl, setDisplayUrl] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);

  // Cropping state
  const [showCropModal, setShowCropModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedFileUrl, setSelectedFileUrl] = useState<string | null>(null);
  const [fixedCrop, setFixedCrop] = useState<PixelCrop | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const cropAreaRef = useRef<HTMLDivElement>(null);

  // Add state for preview URL
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // Get permission error handler from context
  const { getMutationErrorHandler } = usePermissionModal();

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('imagem', file);
      
      // For new users, store the file but don't upload it yet
      // It will be sent together with the user data
      if (isNewUser) {
        // Generate a temporary object URL to display the image
        const tempUrl = URL.createObjectURL(file);
        return tempUrl;
      }
      
      // Normal upload for existing users
      const response = await axios.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (!response.data.isSuccess) {
        throw new Error(response.data.mensagem || 'Erro ao fazer upload da imagem');
      }
      
      return response.data.data.imagemUrl;
    },
    onError: getMutationErrorHandler(
      "Você não tem permissão para alterar imagens de usuário",
      (error) => {
        // Handle non-permission errors locally
        setLocalError(error?.message || 'Erro ao fazer upload da imagem');
      }
    ),
    onSuccess: (newImageUrl: any) => {
      // Update internal state
      setDisplayUrl(newImageUrl);
      
      // If this is a new user, we need to convert the image to a data URL
      // that can be included in the user creation request
      if (isNewUser && selectedFile) {
        const reader = new FileReader();
        reader.onloadend = () => {
          // Store the data URL that can be sent to the server
          const base64data = reader.result as string;
          // This passes the base64 data back to the parent component
          onImageUploaded(base64data);
        };
        reader.readAsDataURL(selectedFile);
      } else {
        // For existing users, just pass back the URL
        onImageUploaded(newImageUrl);
      }
    }
  });

  // Delete mutation with permission error handling 
  const deleteMutation = useMutation({
    mutationFn: async () => {
      // For new users, we just clear the image without an API call
      if (isNewUser) {
        return { isSuccess: true }; // Mock successful response
      }
      
      // Normal delete for existing users
      const response = await axios.delete(endpoint);
      
      if (!response.data.isSuccess) {
        throw new Error(response.data.mensagem || 'Erro ao remover a imagem');
      }
      
      return response.data;
    },
    onError: getMutationErrorHandler(
      "Você não tem permissão para remover imagens de usuário",
      (error) => {
        // Handle non-permission errors locally
        setLocalError(error?.message || 'Erro ao remover a imagem');
      }
    ),
    onSuccess: () => {
      setDisplayUrl(null);
      onImageUploaded(null);
    }
  });

  // Function to handle mouse down on the image for dragging
  const handleMouseDown = (e: React.MouseEvent<HTMLImageElement>) => {
    e.preventDefault();
    setIsDragging(true);
    setDragStart({
      x: e.clientX - imagePosition.x,
      y: e.clientY - imagePosition.y
    });
  };

  // Function to handle mouse move for dragging
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging) return;

    const newX = e.clientX - dragStart.x;
    const newY = e.clientY - dragStart.y;

    setImagePosition({
      x: newX,
      y: newY
    });
  };

  // Function to handle mouse up for ending the drag
  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Also add touch event handlers for mobile devices
  const handleTouchStart = (e: React.TouchEvent<HTMLImageElement>) => {
    if (e.touches.length !== 1) return;
    
    e.preventDefault();
    const touch = e.touches[0];
    
    setIsDragging(true);
    setDragStart({
      x: touch.clientX - imagePosition.x,
      y: touch.clientY - imagePosition.y
    });
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!isDragging || e.touches.length !== 1) return;
    
    const touch = e.touches[0];
    
    const newX = touch.clientX - dragStart.x;
    const newY = touch.clientY - dragStart.y;

    setImagePosition({
      x: newX,
      y: newY
    });
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  // Update local display URL when imageUrl prop changes
  useEffect(() => {
    if (imageUrl) {
      setIsImageLoading(true);
    }
    setDisplayUrl(imageUrl);   
  }, [imageUrl]);
  
  // Clean up object URLs when component unmounts or when they're no longer needed
  useEffect(() => {
    return () => {
      // Clean up temporary URLs when component unmounts
      if (isNewUser && displayUrl) {
        URL.revokeObjectURL(displayUrl);
      }
      if (selectedFileUrl) {
        URL.revokeObjectURL(selectedFileUrl);
      }
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [isNewUser, displayUrl, selectedFileUrl, previewUrl]);

  // Size mapping for component
  const sizeMap = {
    sm: { width: 100, height: 100 },
    md: { width: 150, height: 150 },
    lg: { width: 200, height: 200 },
  };
  
  const containerStyles = {
    width: sizeMap[size].width,
    height: sizeMap[size].height,
    borderRadius: shape === 'circle' ? '50%' : '8px',
    overflow: 'hidden',
  };

  // Initialize crop when image loads with exact dimensions to match the final result
  const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;   
    
    const finalSize = sizeMap[size];
    const imageAspect = width / height;
    const cropAspect = finalSize.width / finalSize.height;
    
    let cropScale = 1;
    if (imageAspect > cropAspect) {
      cropScale = (height * 0.8) / finalSize.height;
    } else {
      cropScale = (width * 0.8) / finalSize.width;
    }
    
    const cropSize = {
      width: finalSize.width * cropScale,
      height: finalSize.height * cropScale
    };
    
    const x = (width - cropSize.width) / 2;
    const y = (height - cropSize.height) / 2;
    
    const newFixedCrop: PixelCrop = {
      unit: 'px',
      x,
      y,
      width: cropSize.width,
      height: cropSize.height
    };
    
    setFixedCrop(newFixedCrop);
    resetTransformations();
  }, [size]);

  // Function to clear all states when the modal is closed
  const resetCropState = () => {
    if (selectedFileUrl) URL.revokeObjectURL(selectedFileUrl);
    setSelectedFileUrl(null);
    setSelectedFile(null);
    setZoom(1);
    setRotation(0);
    setImagePosition({ x: 0, y: 0 });
    setFixedCrop(null);
  };

  // Function to close the crop modal
  const closeModal = () => {
    setShowCropModal(false);
    resetCropState();
  };

  // Function to handle file selection
  const handleFileSelection = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setLocalError(null);
    
    if (!['image/jpeg', 'image/png', 'image/gif'].includes(file.type)) {
      setLocalError('Formato não suportado. Use JPEG, PNG ou GIF.');
      return;
    }
    
    const maxSizeKB = 700;
    if (file.size > maxSizeKB * 1024) {
      setLocalError(`A imagem deve ter no máximo ${maxSizeKB}KB.`);
      return;
    }
    
    // Reset states related to the image and mutation
    uploadMutation.reset();
    resetCropState();
    
    // Set the new selected file
    setSelectedFile(file);
    const fileUrl = URL.createObjectURL(file);
    setSelectedFileUrl(fileUrl);
    
    // Important: always set setShowCropModal after configuring other states
    setShowCropModal(true);
    
    // Important: clear the input field to allow selecting the same file again
    e.target.value = '';
  };

  // Live preview update
  useEffect(() => {
    if (!imgRef.current || !fixedCrop) return;
    
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) return;
      
      // Dimensões finais baseadas no tamanho do componente
      const finalSize = sizeMap[size];
      canvas.width = finalSize.width;
      canvas.height = finalSize.height;
      
      // Preencher com fundo branco
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      const imageElement = imgRef.current;
      
      // Para recorte circular, criar um caminho de clipping
      if (shape === 'circle') {
        ctx.beginPath();
        ctx.arc(finalSize.width / 2, finalSize.height / 2, finalSize.width / 2, 0, Math.PI * 2);
        ctx.closePath();
        ctx.clip();
      }
      
      // Criar um canvas temporário para transformações
      const tempCanvas = document.createElement('canvas');
      const tempCtx = tempCanvas.getContext('2d');
      
      if (!tempCtx) return;
      
      // Definir canvas temporário com as dimensões da imagem original
      tempCanvas.width = imageElement.naturalWidth;
      tempCanvas.height = imageElement.naturalHeight;
      
      // Ponto central do canvas temporário
      const centerX = tempCanvas.width / 2;
      const centerY = tempCanvas.height / 2;
      
      // Aplicar transformações no canvas temporário
      tempCtx.translate(centerX, centerY);
      tempCtx.rotate((rotation * Math.PI) / 180);
      tempCtx.scale(zoom, zoom);
      tempCtx.translate(-centerX, -centerY);
      
      // Considerar o deslocamento de posição
      tempCtx.translate(imagePosition.x, imagePosition.y);
      
      // Desenhar a imagem original no canvas temporário com transformações
      tempCtx.drawImage(imageElement, 0, 0, imageElement.naturalWidth, imageElement.naturalHeight);
      
      // Calcular o fator de escala entre a imagem exibida e a imagem natural
      const scaleX = imageElement.naturalWidth / imageElement.width;
      const scaleY = imageElement.naturalHeight / imageElement.height;
      
      // Calcular a área a ser extraída da imagem transformada
      const sourceX = fixedCrop.x * scaleX;
      const sourceY = fixedCrop.y * scaleY;
      const sourceWidth = fixedCrop.width * scaleX;
      const sourceHeight = fixedCrop.height * scaleY;
      
      // Desenhar a seção recortada do canvas temporário para o canvas final
      ctx.drawImage(
        tempCanvas,
        sourceX, sourceY, sourceWidth, sourceHeight,
        0, 0, finalSize.width, finalSize.height
      );
      
      // Atualizar a prévia
      const previewImg = document.getElementById('image-crop-preview') as HTMLImageElement;
      if (previewImg) {
        previewImg.src = canvas.toDataURL(selectedFile?.type || 'image/png');
      }
    } catch (err) {
      console.error("Error updating preview:", err);
    }
  }, [fixedCrop, zoom, rotation, imagePosition, size, shape, selectedFile?.type]);

  const handleCropComplete = async () => {
    if (!selectedFile || !imgRef.current || !fixedCrop) {
      return;
    }
    
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        setLocalError('Erro ao processar a imagem');
        return;
      }
      
      const finalSize = sizeMap[size];
      canvas.width = finalSize.width;
      canvas.height = finalSize.height;
      
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      const imageElement = imgRef.current;
      
      if (shape === 'circle') {
        ctx.beginPath();
        ctx.arc(finalSize.width / 2, finalSize.height / 2, finalSize.width / 2, 0, Math.PI * 2);
        ctx.closePath();
        ctx.clip();
      }
      
      const tempCanvas = document.createElement('canvas');
      const tempCtx = tempCanvas.getContext('2d');
      
      if (!tempCtx) {
        setLocalError('Erro ao processar a imagem');
        return;
      }
      
      tempCanvas.width = imageElement.naturalWidth;
      tempCanvas.height = imageElement.naturalHeight;
      
      const centerX = tempCanvas.width / 2;
      const centerY = tempCanvas.height / 2;
      
      tempCtx.translate(centerX, centerY);
      tempCtx.rotate((rotation * Math.PI) / 180);
      tempCtx.scale(zoom, zoom);
      tempCtx.translate(-centerX, -centerY);
      
      tempCtx.translate(imagePosition.x, imagePosition.y);
      
      tempCtx.drawImage(imageElement, 0, 0, imageElement.naturalWidth, imageElement.naturalHeight);
      
      const scaleX = imageElement.naturalWidth / imageElement.width;
      const scaleY = imageElement.naturalHeight / imageElement.height;
      
      const sourceX = fixedCrop.x * scaleX;
      const sourceY = fixedCrop.y * scaleY;
      const sourceWidth = fixedCrop.width * scaleX;
      const sourceHeight = fixedCrop.height * scaleY;
      
      ctx.drawImage(
        tempCanvas,
        sourceX, sourceY, sourceWidth, sourceHeight,
        0, 0, finalSize.width, finalSize.height
      );
      
      canvas.toBlob(async (blob) => {
        if (!blob) {
          setLocalError('Erro ao processar a imagem');
          return;
        }
        
        if (blob.size > 700 * 1024) {
          setLocalError(`A imagem excede o limite de 700KB. Tente reduzir o tamanho.`);
          return;
        }
        
        const croppedFile = new File([blob], selectedFile.name, { 
          type: selectedFile.type,
          lastModified: Date.now()
        });
        
        setPreviewUrl(URL.createObjectURL(blob));
        
        // Store the cropped file in the component's state
        setSelectedFile(croppedFile);
        
        // Upload the file
        uploadMutation.mutate(croppedFile);
        
        // Clean up - use resetCropState instead of repeating the code
        closeModal();
      }, selectedFile.type);
    } catch (err) {
      console.error("Error processing crop:", err);
      setLocalError('Ocorreu um erro ao processar a imagem');
    }
  };

  const isUploading = uploadMutation.isPending || deleteMutation.isPending;
  
  useEffect(() => {
    if (!isUploading && previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  }, [isUploading, previewUrl]);

  const handleZoomChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setZoom(parseFloat(e.target.value));
  };
  
  const rotateImage = (degrees: number) => {
    setRotation(prevRotation => (prevRotation + degrees) % 360);
  };

  const resetTransformations = () => {
    setZoom(1);
    setRotation(0);
    setImagePosition({ x: 0, y: 0 });
  };

  const handleRemoveImage = () => {
    if (!displayUrl) return;
    deleteMutation.mutate(undefined);
  };

  // Only show local errors - permission errors are handled by the modal context
  const error = localError || null;

  return (
    <div className={`d-flex flex-column align-items-center ${className}`}>   
      <div 
        className="position-relative" 
        style={containerStyles}
      >
        {displayUrl ? (
          <>
            <img 
              src={displayUrl}
              alt="Imagem carregada"
              className="w-100 h-100"
              style={{ objectFit: 'cover' }}
              onLoad={() => setIsImageLoading(false)}
              onError={(e) => {
                console.error("Image failed to load:", displayUrl);
                setDisplayUrl(null);
                setIsImageLoading(false);
              }}
            />
            {isImageLoading && (
              <div 
                className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                style={{ 
                  backgroundColor: 'rgba(0,0,0,0.3)',
                  borderRadius: shape === 'circle' ? '50%' : '8px'
                }}
              >
                <Spinner animation="border" variant="light" size="sm" />
              </div>
            )}
          </>
        ) : (
          <div 
            className="w-100 h-100 d-flex align-items-center justify-content-center"
            style={{ 
              borderRadius: shape === 'circle' ? '50%' : '8px',
              backgroundColor: 'var(--phoenix-primary-bg-subtle)',
              color: 'var(--phoenix-avatar-name-color)',
              fontSize: `${sizeMap[size].height / 2.5}px`,
              fontWeight: 'bold'
            }}
          >
            {nameInitial ? nameInitial.toUpperCase() : <FontAwesomeIcon icon={faUser} />}
          </div>
        )}
        
        <label 
          className="position-absolute top-0 start-0 w-100 h-100 m-0 d-flex align-items-center justify-content-center"
          style={{ 
            cursor: 'pointer',
            backgroundColor: 'rgba(0,0,0,0)', 
            transition: 'background-color 0.2s',
            borderRadius: shape === 'circle' ? '50%' : '8px',    
          }}
        >
          <input 
            type="file"
            accept="image/jpeg,image/png"
            onChange={handleFileSelection}
            style={{ display: 'none' }}
          />
          
          {isUploading && (
            <div 
              className="w-100 h-100 d-flex align-items-center justify-content-center"
              style={{ 
                backgroundColor: 'rgba(0,0,0,0.5)',
                borderRadius: shape === 'circle' ? '50%' : '8px'
              }}
            >
              <Spinner animation="border" variant="light" />
            </div>
          )}
        </label>
      </div>
      
      <div className="d-flex gap-2 mt-3">
        <label className="btn btn-sm btn-outline-primary">
          {displayUrl ? 'Alterar' : 'Adicionar'}
          <FontAwesomeIcon icon={faPen} className="ms-1" />
          <input 
            type="file"
            accept="image/jpeg,image/png,image/gif"
            onChange={handleFileSelection}
            style={{ display: 'none' }}
          />
        </label>
        
        {displayUrl && (
          <Button
            variant="outline-danger"
            size="sm"
            onClick={handleRemoveImage}
            disabled={isUploading}
          >
            Remover
            <FontAwesomeIcon icon={faTrash} className="ms-1" />
          </Button>
        )}
      </div>
      
      {error && (
        <div className="text-danger small mt-2">
          {error}
        </div>
      )}

      {isNewUser && displayUrl && (
        <div className="text-info small mt-2">
          <FontAwesomeIcon icon={faInfo} className="me-1" />
          A imagem será salva quando o usuário for cadastrado
        </div>
      )}

      <Modal 
        show={showCropModal}
        onHide={closeModal}
        centered
        size="lg"
        dialogClassName="modern-crop-modal"
      >
        <Modal.Header 
          closeButton 
          className="border-0 pb-0"
        >
          <Modal.Title as="h5">
            <FontAwesomeIcon icon={faPen} className="text-primary me-2" />
            Ajustar Imagem
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          <div className="crop-editor-container">
            {selectedFileUrl && (
              <div className="row g-0">
                <div className="col-md-8 p-4 border-end">
                  <div 
                    ref={cropAreaRef}
                    className="crop-area-container mx-auto d-flex justify-content-center align-items-center rounded-3 shadow-sm" 
                    style={{ 
                      backgroundColor: '#f8f9fa',
                      backgroundImage: 'linear-gradient(45deg, #eee 25%, transparent 25%, transparent 75%, #eee 75%, #eee), linear-gradient(45deg, #eee 25%, transparent 25%, transparent 75%, #eee 75%, #eee)',
                      backgroundSize: '16px 16px',
                      backgroundPosition: '0 0, 8px 8px',
                      minHeight: '400px',
                      maxHeight: '70vh',
                      overflow: 'hidden',
                      position: 'relative'
                    }}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                  >
                    <div className="position-relative">
                      <img
                        ref={imgRef}
                        src={selectedFileUrl}
                        alt="Ajustar imagem"
                        style={{ 
                          maxWidth: '100%',
                          maxHeight: '60vh',
                          transform: `
                            translate(${imagePosition.x}px, ${imagePosition.y}px) 
                            scale(${zoom}) 
                            rotate(${rotation}deg)
                          `,
                          transformOrigin: 'center',
                          cursor: 'move',
                          transition: isDragging ? 'none' : 'transform 0.2s ease'
                        }}
                        onLoad={onImageLoad}
                        onMouseDown={handleMouseDown}
                        onTouchStart={handleTouchStart}
                        draggable={false}
                      />
                      
                      {fixedCrop && (
                        <div 
                          className="position-absolute crop-overlay"
                          style={{
                            top: fixedCrop.y,
                            left: fixedCrop.x,
                            width: fixedCrop.width,
                            height: fixedCrop.height,
                            border: '2px solid white',
                            borderRadius: shape === 'circle' ? '50%' : '8px',
                            boxShadow: '0 0 0 2000px rgba(0, 0, 0, 0.7)',
                            pointerEvents: 'none',
                            zIndex: 10
                          }}
                        />
                      )}
                    </div>

                    <div 
                      className="position-absolute top-0 start-0 p-2 m-2 bg-dark bg-opacity-75 text-white rounded-3 instruction-badge" 
                      style={{ fontSize: '0.8rem', zIndex: 20 }}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <FontAwesomeIcon icon={faArrowsAlt} />
                        <span>Arraste para posicionar</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-md-4 p-4 d-flex flex-column">
                  <div className="mb-4">
                    <h6 className="mb-3 text-body-secondary fw-normal">Pré-visualização</h6>
                    <div className="d-flex justify-content-center">
                      <div 
                        className="preview-container border shadow-sm"
                        style={{
                          width: sizeMap[size].width,
                          height: sizeMap[size].height,
                          borderRadius: shape === 'circle' ? '50%' : '8px',
                          overflow: 'hidden',
                          backgroundColor: '#f8f9fa',
                        }}
                      >
                        <img 
                          id="image-crop-preview" 
                          alt="Pré-visualização"
                          className="w-100 h-100"
                          style={{ objectFit: 'cover' }}
                        />
                      </div>
                    </div>
                  </div>

                  <h6 className="mb-3 text-body-secondary fw-normal">Ajustes</h6>
                  
                  <div className="mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div className="d-flex align-items-center">
                        <div className="control-icon me-2">
                          <FontAwesomeIcon icon={faExpand} className="text-primary" />
                        </div>
                        <span className="small">Zoom</span>
                      </div>
                      <span className="badge bg-primary bg-opacity-10 text-primary">{zoom.toFixed(1)}x</span>
                    </div>
                    <div className="d-flex gap-2 align-items-center">
                      <button 
                        className="btn btn-light btn-sm rounded-circle p-1"
                        onClick={() => setZoom(Math.max(zoom - 0.1, 0.5))}
                        title="Diminuir zoom"
                      >
                        <FontAwesomeIcon icon={faTimes} className="small" />
                      </button>
                      <input 
                        type="range" 
                        className="form-range slider-modern flex-grow-1" 
                        min="0.5" 
                        max="3" 
                        step="0.1" 
                        value={zoom}
                        onChange={handleZoomChange}
                      />
                      <button 
                        className="btn btn-light btn-sm rounded-circle p-1"
                        onClick={() => setZoom(Math.min(zoom + 0.1, 3))}
                        title="Aumentar zoom"
                      >
                        <FontAwesomeIcon icon={faExpand} className="small" />
                      </button>
                    </div>
                  </div>

                  <div className="mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div className="d-flex align-items-center">
                        <div className="control-icon me-2">
                          <FontAwesomeIcon icon={faRotateLeft} className="text-primary" />
                        </div>
                        <span className="small">Rotação</span>
                      </div>
                      <span className="badge bg-primary bg-opacity-10 text-primary">{rotation}°</span>
                    </div>
                    <div className="d-flex gap-2">
                      <button 
                        className="btn btn-light btn-sm flex-grow-1"
                        onClick={() => rotateImage(-90)}
                      >
                        <FontAwesomeIcon icon={faRotateLeft} className="me-1" /> 
                        <span>-90°</span>
                      </button>
                      <button 
                        className="btn btn-light btn-sm flex-grow-1"
                        onClick={() => rotateImage(90)}
                      >
                        <span>+90°</span> 
                        <FontAwesomeIcon icon={faRotateLeft} className="ms-1 flip-horizontal" />
                      </button>
                    </div>
                  </div>

                  <button 
                    className="btn btn-link text-decoration-none p-0 mb-3 text-center d-block w-100"
                    onClick={resetTransformations}
                  >
                    <FontAwesomeIcon icon={faArrowsAlt} className="me-1" /> 
                    <span>Centralizar imagem</span>
                  </button>
                  
                  {error && (
                    <div className="alert alert-danger p-2 small mb-0 mt-auto">
                      <FontAwesomeIcon icon={faTimes} className="me-2" />
                      {error}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button 
            variant="outline-secondary" 
            onClick={closeModal}
          >
            Cancelar
          </Button>
          <Button 
            variant="primary" 
            onClick={handleCropComplete}
            disabled={!fixedCrop || isUploading}
          >
            {isUploading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Processando...
              </>
            ) : 'Aplicar'}
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .modern-crop-modal .modal-content {
          border: none;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 8px 30px rgba(0,0,0,0.12);
        }
        
        .crop-area-container {
          transition: all 0.2s ease;
        }
        
        .crop-area-container:hover {
          box-shadow: 0 0 0 1px rgba(0,0,0,0.1);
        }
        
        .slider-modern {
          height: 4px;
          cursor: pointer;
        }
        
        .slider-modern::-webkit-slider-thumb {
          height: 16px;
          width: 16px;
          cursor: pointer;
          background: var(--bs-primary);
          border: 2px solid white;
          box-shadow: 0 1px 3px rgba(0,0,0,0.2);
        }
        
        .btn {
          transition: all 0.15s ease;
        }
        
        .control-icon {
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(var(--bs-primary-rgb), 0.1);
          border-radius: 50%;
        }
        
        .flip-horizontal {
          transform: scaleX(-1);
        }
        
        .instruction-badge {
          animation: fadeInOut 2s ease-in-out;
          animation-iteration-count: 2;
          opacity: 0.8;
        }
        
        .preview-container {
          transition: all 0.2s ease;
        }
        
        .preview-container:hover {
          box-shadow: 0 3px 10px rgba(0,0,0,0.08) !important;
        }
        
        .crop-overlay {
          animation: pulse 2s infinite;
        }
        
        @keyframes fadeInOut {
          0% { opacity: 0; }
          50% { opacity: 0.8; }
          100% { opacity: 0.8; }
        }
        
        @keyframes pulse {
          0% { box-shadow: 0 0 0 2000px rgba(0, 0, 0, 0.7); }
          50% { box-shadow: 0 0 0 2000px rgba(0, 0, 0, 0.65); }
          100% { box-shadow: 0 0 0 2000px rgba(0, 0, 0, 0.7); }
        }
      `}</style>
    </div>
  );
};

export default ImageUploader;
